import { useState, useEffect, useContext } from "react"
import { GlobalContextProvider } from "../components/functions/GlobalContext"
import {
    Box,
    Text,
    Center,
    HStack,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Spinner,
} from "@chakra-ui/react"
import { ChevronLeftIcon } from '@chakra-ui/icons'
import InfoModal from "../components/modals/InfoModal"
import Const from "../Consts"
import { Twirp, TwirpProps } from "../components/functions/Twirp"
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

type MedalRow = {
    id: number
    name: string
    username: string
    gold_count: number
    silver_count: number
    bronze_count: number
}

export default function RantingMedalsPage() {
    const globalContext = useContext(GlobalContextProvider)
    const { t } = useTranslation();
    const navigate = useNavigate()
    const location = useLocation()
    const { rantingid } = useParams()

    const [rantingName, setRantingName] = useState((location.state as any)?.rantingName || "")

    const [rows, setRows] = useState<MedalRow[]>([])
    const [loading, setLoading] = useState(false)

    const [modalData, setModalData] = useState({
        title: "",
        main: "",
        isOpen: false,
    })
    const closeModal = () => {
        setModalData({ ...modalData, isOpen: false })
    }

    const loadRantingName = async () => {
        const props: TwirpProps = {
            url: Const.API_ReadRanting,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 10000,
            data: { id: +(rantingid || "0") }
        }
        const result = await Twirp(props)
        if (result.state == 0) {
            setRantingName(result.data.nama)
        }
    }

    const loadData = async () => {
        setLoading(true)
        const props: TwirpProps = {
            url: Const.API_ListMedalStats,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 10000,
            data: { level: 5, parent_id: +(rantingid || "0") }
        }
        const result = await Twirp(props)
        if (result.state == 2) {//other error
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {//twirp controlled error
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        } else { //successful
            const raw = result.data.Stats || []
            setRows(raw.map((v: any) => ({
                id: +v.id,
                name: v.name,
                username: v.username,
                gold_count: +v.gold_count,
                silver_count: +v.silver_count,
                bronze_count: +v.bronze_count,
            })))
        }
        setLoading(false)
    }

    useEffect(() => {
        let username = globalContext?.GetLoginData().username || ""
        if (username === "") {
            navigate('/login')
            return
        }
        if (rantingName === "") {
            loadRantingName()
        }
        loadData()
    }, [rantingid])

    const onUsernameClick = (pesilatId: number) => {
        navigate("/profileview/" + pesilatId)
    }

    const totals = rows.reduce((acc, r) => ({
        gold: acc.gold + r.gold_count,
        silver: acc.silver + r.silver_count,
        bronze: acc.bronze + r.bronze_count,
    }), { gold: 0, silver: 0, bronze: 0 })

    return (
        <Box py={"10px"} px={"10px"} borderWidth='0px' borderRadius='lg' overflow='hidden'>
            <InfoModal isOpen={modalData.isOpen} title={modalData.title} mainText={modalData.main} closeFunction={closeModal} />

            <HStack spacing={2} mb={"10px"}>
                <Button size='sm' leftIcon={<ChevronLeftIcon />} variant='outline' onClick={() => navigate('/reports')}>
                    {t('Back')}
                </Button>
                <Text fontSize='md' fontWeight={'bold'}>
                    {t('Ranting')}{rantingName !== "" ? ": " + rantingName : ""}
                </Text>
            </HStack>

            {loading ? (
                <Center py={"20px"}>
                    <Spinner size='lg' />
                </Center>
            ) : (
                <TableContainer>
                    <Table variant='simple' colorScheme='blue' size='sm'>
                        <Thead>
                            <Tr>
                                <Th>#</Th>
                                <Th>{t('Username')}</Th>
                                <Th>{t('Full Name')}</Th>
                                <Th isNumeric>{t('Gold')}</Th>
                                <Th isNumeric>{t('Silver')}</Th>
                                <Th isNumeric>{t('Bronze')}</Th>
                                <Th isNumeric>{t('Total')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.length === 0 ? (
                                <Tr>
                                    <Td colSpan={7}>
                                        <Center>{t('no_records')}</Center>
                                    </Td>
                                </Tr>
                            ) : (
                                rows.map((row, index) => (
                                    <Tr key={row.id}>
                                        <Td>{index + 1}</Td>
                                        <Td>
                                            <Button variant='link' colorScheme='blue' size='sm' onClick={() => onUsernameClick(row.id)}>
                                                {row.username}
                                            </Button>
                                        </Td>
                                        <Td>{row.name}</Td>
                                        <Td isNumeric color='yellow.600' fontWeight='bold'>{row.gold_count}</Td>
                                        <Td isNumeric color='gray.500' fontWeight='bold'>{row.silver_count}</Td>
                                        <Td isNumeric color='orange.500' fontWeight='bold'>{row.bronze_count}</Td>
                                        <Td isNumeric fontWeight='bold'>{row.gold_count + row.silver_count + row.bronze_count}</Td>
                                    </Tr>
                                ))
                            )}
                            {rows.length > 0 ? (
                                <Tr bg="gray.50" fontWeight='bold'>
                                    <Td colSpan={3}>{t('Total')}</Td>
                                    <Td isNumeric color='yellow.600'>{totals.gold}</Td>
                                    <Td isNumeric color='gray.500'>{totals.silver}</Td>
                                    <Td isNumeric color='orange.500'>{totals.bronze}</Td>
                                    <Td isNumeric>{totals.gold + totals.silver + totals.bronze}</Td>
                                </Tr>
                            ) : ''}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    )
}
